import { useCallback, useEffect, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreOrder, StoreReviewableProduct } from '../models';

const COMPLETED_ORDER_STATUS = 2;

export function useReviewableProducts(orders: StoreOrder[]) {
  const [reviewableByOrder, setReviewableByOrder] = useState<Record<number, StoreReviewableProduct[]>>({});

  useEffect(() => {
    const completedOrders = orders.filter((o) => o.status === COMPLETED_ORDER_STATUS);
    if (completedOrders.length === 0) return;

    let active = true;

    Promise.all(
      completedOrders.map((order) =>
        storeService
          .getReviewableProducts(order.id)
          .then((products) => [order.id, products] as [number, StoreReviewableProduct[]])
          .catch(() => [order.id, []] as [number, StoreReviewableProduct[]])
      )
    ).then((results) => {
      if (!active) return;
      setReviewableByOrder((prev) => {
        const next = { ...prev };
        results.forEach(([orderId, products]) => {
          next[orderId] = products;
        });
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [orders]);

  const markProductReviewed = useCallback((orderId: number, productId: number) => {
    setReviewableByOrder((prev) => ({
      ...prev,
      [orderId]: (prev[orderId] ?? []).filter((p) => p.productId !== productId),
    }));
  }, []);

  return { reviewableByOrder, markProductReviewed };
}
